// Modal de upgrade de plan — aparece cuando se intenta acceder a un módulo no disponible
import { useState, useEffect } from 'react';
import { X, Lock, Zap, CheckCircle2 } from 'lucide-react';

const MODULE_NAMES: Record<string, string> = {
  visitors:     'Citas de Visitantes',
  bookings:     'Espacios y Reservas',
  parking:      'Gestión de Estacionamientos',
  noc:          'Centro de Operaciones (NOC)',
  reports:      'Reportes de Trazabilidad',
  bulk_import:  'Carga Masiva de Empleados',
  abac:         'Control de Acceso Avanzado (ABAC)',
  biometrics:   'Módulo Biométrico',
  lpr:          'Reconocimiento de Patentes (LPR)',
  internal_api: 'API Interna',
  compliance:   'Cumplimiento Normativo',
};

const PLAN_NAMES: Record<string, string> = {
  starter:    'Starter',
  standard:   'Standard',
  pro:        'Pro',
  enterprise: 'Enterprise',
};

const MODULE_MIN_PLAN: Record<string, string> = {
  bulk_import:  'standard',
  visitors:     'pro',
  bookings:     'pro',
  parking:      'pro',
  noc:          'enterprise',
  reports:      'pro',
  abac:         'pro',
  biometrics:   'enterprise',
  lpr:          'enterprise',
  internal_api: 'enterprise',
  compliance:   'enterprise',
};

// Plan mínimo requerido por tipo de credencial
const CREDENTIAL_TYPE_MIN_PLAN: Record<string, string> = {
  rfid_tag:      'standard',
  tag_vehicular: 'standard',
  qr_employee:   'standard',
  fingerprint:   'enterprise',
  face:          'enterprise',
  license_plate: 'enterprise',
};

const PLAN_FEATURES: Record<string, string[]> = {
  standard:   ['Carga masiva de empleados', 'Hasta 180 empleados', '2 Hubs', '20 Puntos de acceso'],
  pro:        ['Todo Standard', 'Visitantes, Reservas, Parking', 'Reportes avanzados', 'Hasta 600 empleados', '10 Hubs'],
  enterprise: ['Todo Pro', 'Biometría y LPR', 'API interna', 'Cumplimiento normativo', 'Sin límites'],
};

interface PlanBlockedDetail {
  module?: string;
  plan?: string;
  code?: string;
  error?: string;
  message?: string;
  credential_type?: string;
  credential_label?: string;
}

export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<PlanBlockedDetail>({});

  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<PlanBlockedDetail>;
      setDetail(custom.detail || {});
      setOpen(true);
    };
    window.addEventListener('plan:module-blocked', handler);
    return () => window.removeEventListener('plan:module-blocked', handler);
  }, []);

  if (!open) return null;

  const isCredentialBlock = detail.code === 'CREDENTIAL_TYPE_NOT_AVAILABLE';
  const currentPlan = detail.plan ?? 'starter';

  // Qué se está bloqueando (módulo o tipo de credencial)
  const blockLabel = isCredentialBlock
    ? (detail.credential_label ?? detail.credential_type ?? 'este tipo de credencial')
    : (detail.module ? (MODULE_NAMES[detail.module] ?? detail.module) : 'este módulo');

  // Plan mínimo requerido
  const requiredPlan = isCredentialBlock
    ? (CREDENTIAL_TYPE_MIN_PLAN[detail.credential_type ?? ''] ?? 'standard')
    : (detail.module ? (MODULE_MIN_PLAN[detail.module] ?? 'pro') : 'pro');

  const features = PLAN_FEATURES[requiredPlan] ?? [];

  // Título del header
  const headerTitle = isCredentialBlock ? 'Credencial no disponible' : 'Módulo no disponible';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-in animate-zoom-in-95">
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <X size={16} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
            <Lock size={20} className="text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {headerTitle}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Plan actual: <span className="font-medium">{PLAN_NAMES[currentPlan] ?? currentPlan}</span>
            </p>
          </div>
        </div>

        {/* Body */}
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
          <span className="font-medium text-slate-800 dark:text-slate-200">{blockLabel}</span>
          {isCredentialBlock ? ' no está disponible en tu plan actual. Requiere el plan ' : ' está disponible desde el plan '}
          <span className="font-semibold text-blue-600 dark:text-blue-400">
            {PLAN_NAMES[requiredPlan] ?? requiredPlan}
          </span>.
        </p>

        {/* Features del plan requerido */}
        {features.length > 0 && (
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 mb-5">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Zap size={11} />
              Plan {PLAN_NAMES[requiredPlan]} incluye
            </p>
            <ul className="space-y-1.5">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle2 size={14} className="text-green-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => setOpen(false)}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Cerrar
          </button>
          <button
            onClick={() => {
              setOpen(false);
              // Redirigir a página de administración / contacto de ventas
              window.open('mailto:ventas@controlnat.com?subject=Upgrade%20de%20Plan', '_blank');
            }}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors flex items-center justify-center gap-1.5"
          >
            <Zap size={14} />
            Actualizar plan
          </button>
        </div>
      </div>
    </div>
  );
}
